/**
 * The "On the toolbar" list — a dnd-kit sortable list of the primary row.
 *
 * The two anchors are rendered inside the list (so the user sees where Home
 * and More ▾ sit) but are not draggable: `useSortable({ disabled })` keeps
 * them out of the drag collision set, and `reorderToolbarPrimary` pins them
 * again on the way in, so neither a mouse nor a keyboard drop can move them.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { ToolbarItemId } from '../../../model/toolbarItemTypes';
import { toolbarCatalogEntry } from '../../../model/toolbarItemCatalog';
import { ToolbarCustomizationRow } from './ToolbarCustomizationRow';

type RowProps = {
  id: ToolbarItemId;
  unavailable: boolean;
  onToggleRow: (id: ToolbarItemId) => void;
  onHide: (id: ToolbarItemId) => void;
};

function SortableToolbarRow({ id, unavailable, onToggleRow, onHide }: RowProps) {
  const anchored = !toolbarCatalogEntry(id).hideable;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: anchored,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
    >
      <ToolbarCustomizationRow
        id={id}
        placement="row"
        canPromote
        unavailable={unavailable}
        onToggleRow={onToggleRow}
        onHide={onHide}
        dragHandle={
          anchored ? null : (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="flex h-4 w-4 cursor-grab items-center justify-center rounded-sm touch-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
              aria-label={`Drag to reorder ${toolbarCatalogEntry(id).label}`}
              data-testid={`toolbar-customize-drag-${id}`}
            >
              <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )
        }
      />
    </div>
  );
}

export type ToolbarRowListProps = {
  items: ToolbarItemId[];
  /** Ids that would not render for the connections currently open. */
  unavailable: ReadonlySet<ToolbarItemId>;
  onReorder: (activeId: ToolbarItemId, overId: ToolbarItemId) => void;
  onToggleRow: (id: ToolbarItemId) => void;
  onHide: (id: ToolbarItemId) => void;
};

export function ToolbarRowList({
  items,
  unavailable,
  onReorder,
  onToggleRow,
  onHide,
}: ToolbarRowListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onReorder(active.id as ToolbarItemId, over.id as ToolbarItemId);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div
          className="rounded-md border border-border bg-background p-1"
          data-testid="toolbar-customize-row-list"
        >
          {items.map((id) => (
            <SortableToolbarRow
              key={id}
              id={id}
              unavailable={unavailable.has(id)}
              onToggleRow={onToggleRow}
              onHide={onHide}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
